import { Option } from "@inrev/core";
import {
	type ExperianCreditDetail,
	formatMonthYear,
} from "@portal/api/account/admin/individualCreditReport";
import { Card, Icon, Tag, Text } from "@portal/components/primitives";
import type { FC, ReactNode } from "react";
import { ReportSection, StatusSummary } from "./ReportSection";

type Detail = ExperianCreditDetail;
type Identity = Detail["identity"];

const reportedRange = (first: string | undefined, last: string | undefined, current: boolean) => {
	if (current) return Option.isSome(first) ? `Since ${formatMonthYear(first)}` : "Current";
	if (Option.isSome(first) && Option.isSome(last))
		return `${formatMonthYear(first)} – ${formatMonthYear(last)}`;
	return Option.map(first ?? last, (date) => `Reported ${formatMonthYear(date)}`);
};

const IdentityCard: FC<{ label: string; testId: string; children: ReactNode }> = ({
	label,
	testId,
	children,
}) => (
	<Card className="flex flex-col gap-3.5 rounded-xl px-5 py-4" data-testid={testId}>
		<Text type="eyebrow" color="tertiary">
			{label}
		</Text>
		{children}
	</Card>
);

const EmptyLine: FC<{ children: ReactNode }> = ({ children }) => (
	<Text type="body" color="tertiary">
		{children}
	</Text>
);

type MatchLineProps = { matches: boolean | undefined; label: string };

const MatchLine: FC<MatchLineProps> = ({ matches, label }) =>
	Option.isNone(matches) ? null : (
		<div className="flex items-center gap-2">
			<Icon
				type={matches ? "check-circle" : "exclamation-triangle"}
				size="mini"
				color={matches ? "confirm" : "warning"}
			/>
			<Text type="body" color="primary">
				{label} {matches ? "matches" : "doesn't match"} submitted PII
			</Text>
		</div>
	);

const identitySummary = (identity: Identity) => {
	const checks = [identity.nameMatches, identity.addressMatches].filter(Option.isSome);
	if (checks.length === 0) return undefined;
	if (checks.every(Boolean)) {
		return <StatusSummary tone="confirm">Identity matches submission</StatusSummary>;
	}
	const mismatched = [
		identity.nameMatches === false ? "Name" : undefined,
		identity.addressMatches === false ? "Current address" : undefined,
	].filter(Option.isSome);
	return (
		<StatusSummary tone="warning">{`${mismatched.join(" and ")} differs from submission`}</StatusSummary>
	);
};

export const IdentitySection: FC<{ detail: Detail }> = ({ detail }) => {
	const { identity } = detail;
	return (
		<ReportSection
			title="Identity & Stability"
			testId="individual_credit_identity"
			summary={identitySummary(identity)}
		>
			<div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
				<IdentityCard label="Address History" testId="individual_credit_addresses">
					{identity.addresses.length === 0 && <EmptyLine>No addresses reported</EmptyLine>}
					{identity.addresses.map((address) => (
						<div
							key={`${address.line1}-${address.firstReportedOn}`}
							className="flex items-start justify-between gap-3"
						>
							<div className="flex flex-col gap-2.5">
								<Text type="body-strong" color="primary">
									{address.line1}
								</Text>
								<Text type="caption" color="tertiary">
									{[
										address.line2,
										reportedRange(address.firstReportedOn, address.lastReportedOn, address.current),
									]
										.filter(Option.isSome)
										.join(" · ")}
								</Text>
							</div>
							{address.current && <Tag color="blue">Current</Tag>}
						</div>
					))}
				</IdentityCard>
				<div className="flex flex-col gap-3">
					<IdentityCard label="Employment History" testId="individual_credit_employers">
						{identity.employers.length === 0 && <EmptyLine>No employers reported</EmptyLine>}
						{identity.employers.map((employer) => (
							<div
								key={`${employer.name}-${employer.lastReportedOn}`}
								className="flex items-baseline justify-between gap-3"
							>
								<Text type="body-strong" color="primary">
									{employer.name}
								</Text>
								{Option.isSome(employer.lastReportedOn ?? employer.firstReportedOn) && (
									<Text type="caption" color="secondary" className="whitespace-nowrap">
										Reported {formatMonthYear(employer.lastReportedOn ?? employer.firstReportedOn)}
									</Text>
								)}
							</div>
						))}
					</IdentityCard>
					<IdentityCard label="Name & Address Match" testId="individual_credit_name_match">
						<MatchLine matches={identity.nameMatches} label="Name" />
						<MatchLine matches={identity.addressMatches} label="Current address" />
						<div className="flex flex-wrap items-center gap-2">
							<Text type="caption" color="tertiary">
								Also reported as
							</Text>
							{identity.akas.length === 0 ? (
								<Text type="caption" color="secondary">
									No other names
								</Text>
							) : (
								identity.akas.map((aka) => (
									<Tag key={aka} color="gray">
										{aka}
									</Tag>
								))
							)}
						</div>
					</IdentityCard>
				</div>
			</div>
		</ReportSection>
	);
};
